import { Config, isLoopbackUrl } from "@amtana/module";
import { z } from "zod";

// Where a mailed verification link points (the platform's own origin, never
// guessed from a request header), and the secret a session's cookie is
// signed with (ADR-004 §2): a tampered or guessed value is refused before
// any row is looked up. One secret, held only by the process that boots it.

/** Nobody set AUTH_SESSION_SECRET; refused once NODE_ENV is "production" (ADR-004 §6). */
export const DEV_SESSION_SECRET = "a".repeat(32);

/** oidcsim's own clients, playing Google and LinkedIn (sims/oidcsim/adrs/001); refused once
 * NODE_ENV is "production". */
export const DEV_GOOGLE_CLIENT_ID = "googlesim-client.apps.googleusercontent.com";
const DEV_GOOGLE_CLIENT_SECRET = "googlesim-secret";
export const DEV_LINKEDIN_CLIENT_ID = "linkedinsim-client";
const DEV_LINKEDIN_CLIENT_SECRET = "linkedinsim-secret";

/** Cloudflare's published test key pair for Turnstile (ADR-025 §8): `siteverify` always answers
 * yes for this secret, so a developer can turn the challenge on locally with no simulator. Never
 * a real secret; refused once NODE_ENV is "production". */
export const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";
export const TURNSTILE_ALWAYS_PASS_SECRET = "1x0000000000000000000000000000000AA";

export const authConfig = Config.define((c) => ({
  platformOrigin: c.env("PLATFORM_ORIGIN", z.url().default("http://127.0.0.1:4312")),
  sessionSecret: c.env(
    "AUTH_SESSION_SECRET",
    z
      .string()
      .min(32, "needs 32 characters or more: a session's signature is only as strong as this secret.")
      .default(DEV_SESSION_SECRET),
    {
      refuseInProduction: (value) =>
        value === DEV_SESSION_SECRET
          ? "still the development default; a session's signature is only as strong as this secret"
          : undefined,
    },
  ),
  /**
   * Google sign-in (ADR-007): the OpenID Connect issuer, `https://accounts.google.com` in
   * production, oidcsim playing Google in development and tests. Google sends the browser back
   * to PLATFORM_ORIGIN/auth/google/callback, which must be registered with this client.
   */
  googleIssuer: c.env("GOOGLE_ISSUER", z.url().default("http://127.0.0.1:4324"), {
    refuseInProduction: (value) =>
      isLoopbackUrl(value) ? "points at a loopback host, the Google simulator" : undefined,
  }),
  googleClientId: c.env("GOOGLE_CLIENT_ID", z.string().min(1).default(DEV_GOOGLE_CLIENT_ID), {
    refuseInProduction: (value) =>
      value === DEV_GOOGLE_CLIENT_ID ? "is the Google simulator's client, not a real one" : undefined,
  }),
  googleClientSecret: c.env("GOOGLE_CLIENT_SECRET", z.string().min(1).default(DEV_GOOGLE_CLIENT_SECRET), {
    refuseInProduction: (value) =>
      value === DEV_GOOGLE_CLIENT_SECRET ? "is the Google simulator's secret, not a real one" : undefined,
  }),
  /**
   * LinkedIn sign-in (ADR-007, revision 2): "Sign In with LinkedIn using OpenID Connect", issuer
   * `https://www.linkedin.com/oauth` in production, oidcsim playing LinkedIn in development and
   * tests. LinkedIn sends the browser back to PLATFORM_ORIGIN/auth/linkedin/callback, which must
   * be an authorized redirect URL of this app.
   */
  linkedinIssuer: c.env("LINKEDIN_ISSUER", z.url().default("http://127.0.0.1:4325/oauth"), {
    refuseInProduction: (value) =>
      isLoopbackUrl(value) ? "points at a loopback host, the LinkedIn simulator" : undefined,
  }),
  linkedinClientId: c.env("LINKEDIN_CLIENT_ID", z.string().min(1).default(DEV_LINKEDIN_CLIENT_ID), {
    refuseInProduction: (value) =>
      value === DEV_LINKEDIN_CLIENT_ID ? "is the LinkedIn simulator's client, not a real one" : undefined,
  }),
  linkedinClientSecret: c.env(
    "LINKEDIN_CLIENT_SECRET",
    z.string().min(1).default(DEV_LINKEDIN_CLIENT_SECRET),
    {
      refuseInProduction: (value) =>
        value === DEV_LINKEDIN_CLIENT_SECRET
          ? "is the LinkedIn simulator's secret, not a real one"
          : undefined,
    },
  ),
  /**
   * The sign-up form's challenge (ADR-025 §8, D716): Cloudflare Turnstile, managed mode. Empty
   * means off, which is the default: a release ships before the widget exists, and production
   * stays off until a person sets the real secret (`dev/docs/ops/secrets.md`, "Turn on the
   * sign-up challenge").
   */
  turnstileSecret: c.env("TURNSTILE_SECRET", z.string().default(""), {
    refuseInProduction: (value) =>
      value === TURNSTILE_ALWAYS_PASS_SECRET
        ? "is Cloudflare's always-pass test secret, not a real one"
        : undefined,
  }),
  turnstileSiteKey: c.env("TURNSTILE_SITE_KEY", z.string().default("")),
  /** Where the api checks a token; Cloudflare's own address, never a sim (there is none). */
  turnstileVerifyUrl: c.env(
    "TURNSTILE_VERIFY_URL",
    z.url().default("https://challenges.cloudflare.com/turnstile/v0/siteverify"),
  ),
}));
